import React, { useEffect, useState } from 'react';
import { getPiezasDashboard } from '../../api';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getAccounts, getTransportLines } from '../../api';
import { Autocomplete, TextField, Checkbox } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import ChartDataLabels from 'chartjs-plugin-datalabels';

// Registrar el plugin solo una vez, fuera del componente
if (!ChartJS.registry.plugins.get('datalabels')) {
  ChartJS.register(ChartDataLabels);
}

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PiezasRecibidasDashboard = () => {
  const [data, setData] = useState({ total_enviadas: 0, motivos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [lineasSeleccionadas, setLineasSeleccionadas] = useState([]);
  const [filtros, setFiltros] = useState({
    cliente: '',
    fecha_carga: [null, null],
    fecha_entrega: [null, null],
    linea_transporte: '',
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Paleta de colores sobria y moderna
  const palette = {
    blue: '#2563eb',
    green: '#059669',
    red: '#dc2626',
    yellow: '#fbbf24',
    grayBg: '#f8fafc',
    grayBorder: '#e5e7eb',
    grayText: '#334155',
    cardShadow: '0 2px 12px 0 rgba(31, 41, 55, 0.08)',
  };

  // Cargar clientes y líneas de transporte
  useEffect(() => {
    getAccounts().then(setClientes).catch(() => setClientes([]));
    getTransportLines()
      .then((res) => {
        // Si la respuesta es un objeto, conviértelo a array
        if (Array.isArray(res)) {
          setLineas(res);
        } else if (res && typeof res === 'object') {
          setLineas(Object.values(res));
        } else {
          setLineas([]);
        }
      })
      .catch(() => setLineas([]));
  }, []);

  // Cargar datos con filtros
  const fetchData = async (filtrosToUse = filtros) => {
    setLoading(true);
    setError(null);
    try {
      const [fecha_carga_inicio, fecha_carga_fin] = filtrosToUse.fecha_carga;
      const [fecha_entrega_inicio, fecha_entrega_fin] = filtrosToUse.fecha_entrega;
      const result = await getPiezasDashboard({
        cliente: clientesSeleccionados.map(c => c.cuenta || c.nombre_cuenta).join(','),
        linea_transporte: lineasSeleccionadas.map(l => l.name).join(','),
        fecha_carga_inicio: fecha_carga_inicio ? fecha_carga_inicio.toISOString().slice(0, 10) : undefined,
        fecha_carga_fin: fecha_carga_fin ? fecha_carga_fin.toISOString().slice(0, 10) : undefined,
        fecha_entrega_inicio: fecha_entrega_inicio ? fecha_entrega_inicio.toISOString().slice(0, 10) : undefined,
        fecha_entrega_fin: fecha_entrega_fin ? fecha_entrega_fin.toISOString().slice(0, 10) : undefined,
      });
      setData(result);
    } catch (err) {
      setError('Error al cargar los datos del dashboard.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleFiltroChange = (e) => {
    const { name, value } = e.target;
    setFiltros((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateRangeChange = (name, dates) => {
    setFiltros((prev) => ({ ...prev, [name]: dates }));
  };

  const handleBuscar = () => {
    fetchData(filtros);
  };

  // Color institucional para todas las barras
  const barData = {
    labels: data.motivos.map((item) => item.motivo),
    datasets: [
      {
        label: 'Piezas Rechazadas',
        data: data.motivos.map((item) => item.rechazadas),
        backgroundColor: palette.blue,
        borderRadius: 6,
        borderSkipped: false,
        barPercentage: 0.45,
        categoryPercentage: 0.6,
        borderWidth: 0,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
        labels: {
          color: palette.grayText,
          font: { size: 13, family: 'inherit' },
        },
      },
      title: {
        display: true,
        text: 'Piezas Rechazadas por Motivo',
        color: palette.grayText,
        font: { size: 20, weight: 'bold', family: 'inherit' },
        padding: { top: 10, bottom: 18 },
      },
      tooltip: {
        backgroundColor: '#fff',
        titleColor: '#222',
        bodyColor: '#222',
        borderColor: palette.grayBorder,
        borderWidth: 1,
        titleFont: { weight: 'bold', size: 15 },
        bodyFont: { size: 14 },
        padding: 10,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const motivo = context.label;
            const valor = context.parsed.y;
            const porcentaje = data.motivos[context.dataIndex]?.porcentaje || 0;
            return ` ${motivo}: ${valor} piezas (${porcentaje}%)`;
          },
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: palette.grayText,
        backgroundColor: null,
        borderRadius: 0,
        font: { weight: 'bold', size: 12, family: 'inherit' },
        formatter: (value) => value > 0 ? value : '',
        display: true,
        padding: { top: 0, bottom: 4 },
        borderWidth: 0,
        opacity: 1,
      },
    },
    animation: {
      duration: 600,
      easing: 'easeOutCubic',
    },
    layout: {
      padding: { left: 8, right: 8, top: 0, bottom: 0 },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          color: palette.grayText,
          font: { size: 13, weight: 'bold', family: 'inherit' },
          padding: 6,
        },
      },
      y: {
        beginAtZero: true,
        grid: { color: palette.grayBorder, borderDash: [4, 4] },
        ticks: {
          color: palette.grayText,
          font: { size: 13, weight: 'bold', family: 'inherit' },
          callback: function(value) {
            return value.toLocaleString();
          },
          padding: 6,
        },
      },
    },
    maintainAspectRatio: false,
  };

  // Calcular totales para el resumen
  const totalRechazadas = data.motivos.reduce((acc, m) => acc + m.rechazadas, 0);
  const porcentajeGlobal = data.total_enviadas > 0 ? ((totalRechazadas / data.total_enviadas) * 100).toFixed(2) : '0.00';
  const totalEfectivas = data.total_enviadas - totalRechazadas;
  const tasaEntregaEfectiva = data.total_enviadas > 0 ? ((totalEfectivas / data.total_enviadas) * 100).toFixed(2) : '0.00';

  // Función para manejar el click en el encabezado de la tabla
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Ordenar los motivos según el estado de sortConfig
  const sortedMotivos = React.useMemo(() => {
    if (!sortConfig.key) return data.motivos;
    const sorted = [...data.motivos].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      // Si es porcentaje, convertir a número
      if (sortConfig.key === 'porcentaje') {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [data.motivos, sortConfig]);

  return (
    <div>
      <h2 className="text-xl font-bold mb-4">Piezas Enviadas vs Rechazadas por Motivo</h2>
      {/* Filtros */}
      <div className="flex flex-wrap gap-4 mb-6 items-end">
        <div style={{ minWidth: 250 }}>
          <label className="block text-sm font-medium mb-1">Cliente(s)</label>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={clientes}
            getOptionLabel={(option) => option.cuenta || option.nombre_cuenta || ''}
            value={clientesSeleccionados}
            onChange={(_, value) => setClientesSeleccionados(value)}
            isOptionEqualToValue={(option, value) => (option.cuenta || option.nombre_cuenta) === (value.cuenta || value.nombre_cuenta)}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.cuenta || option.nombre_cuenta}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar y seleccionar cliente(s)" size="small" />
            )}
            sx={{ minWidth: 250 }}
            renderTags={(selected, getTagProps) => (
              <span className="text-gray-700 text-sm px-2">
                {selected.length === 0
                  ? ''
                  : selected.length === 1
                  ? '1 seleccionado'
                  : `${selected.length} seleccionados`}
              </span>
            )}
          />
        </div>
        <div style={{ minWidth: 250 }}>
          <label className="block text-sm font-medium mb-1">Línea de Transporte(s)</label>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={lineas}
            getOptionLabel={(option) => option.name || option.Lt || option.razon_Social || option.vendor || ''}
            value={lineasSeleccionadas}
            onChange={(_, value) => setLineasSeleccionadas(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.name || option.Lt || option.razon_Social || option.vendor}
              </li>
            )}
            renderInput={(params) => (
              <TextField {...params} placeholder="Buscar y seleccionar línea(s)" size="small" />
            )}
            sx={{ minWidth: 250 }}
            renderTags={(selected, getTagProps) => (
              <span className="text-gray-700 text-sm px-2">
                {selected.length === 0
                  ? ''
                  : selected.length === 1
                  ? '1 seleccionada'
                  : `${selected.length} seleccionadas`}
              </span>
            )}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fecha de Carga (rango)</label>
          <DatePicker
            selectsRange
            startDate={filtros.fecha_carga[0]}
            endDate={filtros.fecha_carga[1]}
            onChange={(dates) => handleDateRangeChange('fecha_carga', dates)}
            dateFormat="yyyy-MM-dd"
            className="border rounded px-2 py-1"
            placeholderText="Selecciona rango"
            isClearable
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fecha de Entrega (rango)</label>
          <DatePicker
            selectsRange
            startDate={filtros.fecha_entrega[0]}
            endDate={filtros.fecha_entrega[1]}
            onChange={(dates) => handleDateRangeChange('fecha_entrega', dates)}
            dateFormat="yyyy-MM-dd"
            className="border rounded px-2 py-1"
            placeholderText="Selecciona rango"
            isClearable
          />
        </div>
        <button
          onClick={handleBuscar}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
        >
          Buscar
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-red-500 text-center">{error}</div>
      ) : (
        <>
          {/* Resumen visual de totales */}
          <div className="flex flex-wrap gap-6 mb-8">
            <div style={{
              background: '#fff',
              border: `1px solid ${palette.blue}22`,
              boxShadow: palette.cardShadow,
              borderRadius: 16,
              padding: '1.5rem 2rem',
              minWidth: 180,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <span style={{ color: palette.blue, fontWeight: 600, fontSize: 17 }}>Total Enviado</span>
              <span style={{ color: palette.grayText, fontWeight: 700, fontSize: 28, marginTop: 4 }}>{data.total_enviadas.toLocaleString()}</span>
            </div>
            <div style={{
              background: '#fff',
              border: `1px solid ${palette.green}22`,
              boxShadow: palette.cardShadow,
              borderRadius: 16,
              padding: '1.5rem 2rem',
              minWidth: 180,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <span style={{ color: palette.green, fontWeight: 600, fontSize: 17 }}>Total Entregado Efectivo</span>
              <span style={{ color: palette.grayText, fontWeight: 700, fontSize: 28, marginTop: 4 }}>{totalEfectivas.toLocaleString()}</span>
            </div>
            <div style={{
              background: '#fff',
              border: `1px solid ${palette.red}22`,
              boxShadow: palette.cardShadow,
              borderRadius: 16,
              padding: '1.5rem 2rem',
              minWidth: 180,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <span style={{ color: palette.red, fontWeight: 600, fontSize: 17 }}>Total Rechazado</span>
              <span style={{ color: palette.grayText, fontWeight: 700, fontSize: 28, marginTop: 4 }}>{totalRechazadas.toLocaleString()}</span>
            </div>
            <div style={{
              background: '#fff',
              border: `1px solid ${palette.yellow}22`,
              boxShadow: palette.cardShadow,
              borderRadius: 16,
              padding: '1.5rem 2rem',
              minWidth: 180,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <span style={{ color: palette.yellow, fontWeight: 600, fontSize: 17 }}>% Global de Rechazo</span>
              <span style={{ color: palette.grayText, fontWeight: 700, fontSize: 28, marginTop: 4 }}>{porcentajeGlobal}%</span>
            </div>
            <div style={{
              background: '#fff',
              border: `1px solid ${palette.green}22`,
              boxShadow: palette.cardShadow,
              borderRadius: 16,
              padding: '1.5rem 2rem',
              minWidth: 180,
              display: 'flex', flexDirection: 'column', alignItems: 'center',
            }}>
              <span style={{ color: palette.green, fontWeight: 600, fontSize: 17 }}>Tasa de Entrega Efectiva</span>
              <span style={{ color: palette.grayText, fontWeight: 700, fontSize: 28, marginTop: 4 }}>{tasaEntregaEfectiva}%</span>
            </div>
          </div>
          <div className="mb-8" style={{ minHeight: 350 }}>
            <Bar data={barData} options={barOptions} height={350} />
          </div>
          <div className="overflow-x-auto">
            <table
              className="min-w-full bg-white rounded-xl shadow-sm"
              style={{ borderCollapse: 'separate', borderSpacing: 0, fontFamily: 'inherit' }}
            >
              <thead>
                <tr style={{ background: palette.grayBg }}>
                  <th
                    onClick={() => handleSort('motivo')}
                    style={{ padding: '12px 18px', color: palette.grayText, fontWeight: 700, fontSize: 15, borderBottom: `2px solid ${palette.grayBorder}`, textAlign: 'left', cursor: 'pointer', userSelect: 'none' }}
                  >
                    Motivo{' '}
                    {sortConfig.key === 'motivo' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th
                    onClick={() => handleSort('rechazadas')}
                    style={{ padding: '12px 18px', color: palette.grayText, fontWeight: 700, fontSize: 15, borderBottom: `2px solid ${palette.grayBorder}`, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                  >
                    Piezas Rechazadas{' '}
                    {sortConfig.key === 'rechazadas' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th
                    onClick={() => handleSort('porcentaje')}
                    style={{ padding: '12px 18px', color: palette.grayText, fontWeight: 700, fontSize: 15, borderBottom: `2px solid ${palette.grayBorder}`, textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
                  >
                    % sobre Total Enviado{' '}
                    {sortConfig.key === 'porcentaje' ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedMotivos.map((item, idx) => (
                  <tr
                    key={idx}
                    style={{
                      background: idx % 2 === 0 ? '#fff' : palette.grayBg,
                      transition: 'background 0.2s',
                    }}
                    className="hover:bg-blue-50 group"
                  >
                    <td style={{ padding: '12px 18px', color: palette.grayText, fontWeight: 500, borderBottom: `1px solid ${palette.grayBorder}` }}>{item.motivo}</td>
                    <td style={{ padding: '12px 18px', color: palette.red, fontWeight: 600, textAlign: 'right', borderBottom: `1px solid ${palette.grayBorder}` }}>{item.rechazadas.toLocaleString()}</td>
                    <td style={{ padding: '12px 18px', color: palette.blue, fontWeight: 600, textAlign: 'right', borderBottom: `1px solid ${palette.grayBorder}` }}>{item.porcentaje}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PiezasRecibidasDashboard; 